// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

/**
 * Client-side event log — pure functions to replay events into ledger state.
 * Mirrors packages/core/src/event-log.ts but runs in the browser without Node deps.
 */

import { v4 as uuidv4 } from 'uuid';
import type {
  LedgerEvent,
  LedgerState,
  LedgerSettings,
  EventType,
  EventPayload,
  Account,
  Transaction,
  ScheduledTransaction,
  Budget,
  Price,
  Split,
  AccountCreatedPayload,
  AccountUpdatedPayload,
  AccountDeletedPayload,
  TransactionPostedPayload,
  TransactionVoidedPayload,
  TransactionDeletedPayload,
  SplitReconciledPayload,
  ReconciliationPostedPayload,
  ReconciliationCompletedPayload,
  ScheduledTransactionCreatedPayload,
  ScheduledTransactionUpdatedPayload,
  ScheduledTransactionDeletedPayload,
  BudgetCreatedPayload,
  BudgetUpdatedPayload,
  BudgetDeletedPayload,
  PriceAddedPayload,
  PriceUpdatedPayload,
  PriceDeletedPayload,
  SettingsUpdatedPayload,
  SettingUpdatedPayload,
  CredentialRevokedPayload,
  SettingsValues,
} from '../types.js';
import { SETTINGS_DEFAULTS } from '../types.js';
import { getCurrencyDecimalPlaces } from './currencies.js';
import { getDisplayLocale } from './formatLocale.js';

/** Compute a SHA-256 checksum for event integrity. */
export async function computeChecksum(event: Omit<LedgerEvent, 'checksum'>): Promise<string> {
  const data = event.id + event.sequence + event.type + event.timestamp + JSON.stringify(event.payload);
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(data));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Build a new LedgerEvent with a computed checksum. */
export async function buildEvent(
  type: EventType,
  payload: EventPayload,
  nextSequence: number,
): Promise<LedgerEvent> {
  const base = {
    id: uuidv4(),
    sequence: nextSequence,
    type,
    timestamp: new Date().toISOString(),
    payload,
  };
  const checksum = await computeChecksum(base);
  return { ...base, checksum };
}

/** Validate that splits sum to zero — the fundamental double-entry constraint. */
export function validateSplitsBalance(splits: Split[]): void {
  const total = splits.reduce((sum, s) => sum + s.amount, 0);
  if (total !== 0) {
    throw new Error(
      `Transaction splits do not balance: sum is ${total} (must be 0). Debits must equal credits.`,
    );
  }
}

/**
 * Replay all events to produce the current ledger state.
 * DECISION: balances are tracked in minor units (integer cents) to avoid
 * floating-point precision errors.
 */
export function replayEvents(events: LedgerEvent[]): LedgerState {
  const accounts = new Map<string, Account>();
  const transactions = new Map<string, Transaction>();
  const scheduledTransactions = new Map<string, ScheduledTransaction>();
  const budgets = new Map<string, Budget>();
  const prices = new Map<string, Price>();
  let settings: LedgerSettings = { homeCurrency: 'USD' };
  let lastSequence = 0;

  const sorted = [...events].sort((a, b) => a.sequence - b.sequence);

  for (const event of sorted) {
    lastSequence = event.sequence;

    switch (event.type) {
      case 'AccountCreated': {
        const p = event.payload as AccountCreatedPayload;
        accounts.set(p.accountId, {
          id: p.accountId,
          name: p.name,
          type: p.type,
          currency: p.currency,
          parentId: p.parentId,
          description: p.description,
          code: p.code ?? '',
          hidden: p.hidden ?? false,
          placeholder: p.placeholder,
          ...(p.commodity !== undefined ? { commodity: p.commodity } : {}),
          balance: p.openingBalance ?? 0,
        });
        break;
      }

      case 'AccountUpdated': {
        const p = event.payload as AccountUpdatedPayload;
        const existing = accounts.get(p.accountId);
        if (!existing) break;
        if (p.name !== undefined) existing.name = p.name;
        if (p.description !== undefined) existing.description = p.description;
        if (p.code !== undefined) existing.code = p.code;
        if (p.hidden !== undefined) existing.hidden = p.hidden;
        if (p.placeholder !== undefined) existing.placeholder = p.placeholder;
        if ('parentId' in p) existing.parentId = p.parentId ?? null;
        if (p.commodity !== undefined) existing.commodity = p.commodity;
        break;
      }

      case 'AccountDeleted': {
        const p = event.payload as AccountDeletedPayload;
        accounts.delete(p.accountId);
        if (p.reparentChildrenToId !== undefined) {
          for (const account of accounts.values()) {
            if (account.parentId === p.accountId) {
              account.parentId = p.reparentChildrenToId;
            }
          }
        }
        break;
      }

      case 'TransactionPosted': {
        const p = event.payload as TransactionPostedPayload;
        transactions.set(p.transactionId, {
          id: p.transactionId,
          date: p.date,
          dateEntered: p.dateEntered ?? event.timestamp,
          description: p.description,
          num: p.num ?? '',
          notes: p.notes,
          currency: p.currency,
          splits: p.splits.map((s) => ({ ...s })),
          voided: false,
          voidReason: null,
          ...(p.fitid !== undefined ? { fitid: p.fitid } : {}),
        });
        for (const split of p.splits) {
          const account = accounts.get(split.accountId);
          if (account) {
            account.balance += split.amount;
          }
        }
        break;
      }

      case 'TransactionVoided': {
        const p = event.payload as TransactionVoidedPayload;
        const tx = transactions.get(p.transactionId);
        if (!tx || tx.voided) break;
        tx.voided = true;
        tx.voidReason = p.voidReason;
        for (const split of tx.splits) {
          const account = accounts.get(split.accountId);
          if (account) {
            account.balance -= split.amount;
          }
        }
        break;
      }

      case 'TransactionDeleted': {
        const p = event.payload as TransactionDeletedPayload;
        const tx = transactions.get(p.transactionId);
        if (tx && !tx.voided) {
          for (const split of tx.splits) {
            const account = accounts.get(split.accountId);
            if (account) {
              account.balance -= split.amount;
            }
          }
        }
        transactions.delete(p.transactionId);
        break;
      }

      case 'SplitReconciled': {
        const p = event.payload as SplitReconciledPayload;
        const tx = transactions.get(p.transactionId);
        if (!tx) break;
        const split = tx.splits.find((s) => s.splitId === p.splitId);
        if (split) {
          split.reconcileState = p.reconcileState;
          split.reconcileDate = p.reconcileDate;
        }
        break;
      }

      case 'ReconciliationPosted':
      case 'ReconciliationCompleted': {
        const p = event.payload as ReconciliationPostedPayload | ReconciliationCompletedPayload;
        const reconciledIds = new Set(p.reconciledSplitIds);
        for (const tx of transactions.values()) {
          for (const split of tx.splits) {
            if (split.accountId === p.accountId && reconciledIds.has(split.splitId)) {
              split.reconcileState = 'y';
              split.reconcileDate = p.statementDate;
            }
          }
        }
        break;
      }

      case 'ReconciliationStarted':
        break;

      case 'ScheduledTransactionCreated': {
        const p = event.payload as ScheduledTransactionCreatedPayload;
        scheduledTransactions.set(p.scheduledTransactionId, {
          id: p.scheduledTransactionId,
          name: p.name,
          enabled: p.enabled,
          autoCreate: p.autoCreate,
          autoCreateDaysInAdvance: p.autoCreateDaysInAdvance,
          advanceReminderDays: p.advanceReminderDays,
          templateTransaction: p.templateTransaction,
          frequency: p.frequency,
        });
        break;
      }

      case 'ScheduledTransactionUpdated': {
        const p = event.payload as ScheduledTransactionUpdatedPayload;
        const st = scheduledTransactions.get(p.scheduledTransactionId);
        if (!st) break;
        if (p.name !== undefined) st.name = p.name;
        if (p.enabled !== undefined) st.enabled = p.enabled;
        if (p.autoCreate !== undefined) st.autoCreate = p.autoCreate;
        if (p.autoCreateDaysInAdvance !== undefined) st.autoCreateDaysInAdvance = p.autoCreateDaysInAdvance;
        if (p.advanceReminderDays !== undefined) st.advanceReminderDays = p.advanceReminderDays;
        if (p.templateTransaction !== undefined) st.templateTransaction = p.templateTransaction;
        if (p.frequency !== undefined) st.frequency = p.frequency;
        break;
      }

      case 'ScheduledTransactionDeleted': {
        const p = event.payload as ScheduledTransactionDeletedPayload;
        scheduledTransactions.delete(p.scheduledTransactionId);
        break;
      }

      case 'BudgetCreated': {
        const p = event.payload as BudgetCreatedPayload;
        budgets.set(p.budgetId, {
          id: p.budgetId,
          name: p.name,
          description: p.description,
          periodType: p.periodType,
          numPeriods: p.numPeriods,
          startDate: p.startDate,
          values: p.values,
        });
        break;
      }

      case 'BudgetUpdated': {
        const p = event.payload as BudgetUpdatedPayload;
        const budget = budgets.get(p.budgetId);
        if (!budget) break;
        if (p.name !== undefined) budget.name = p.name;
        if (p.description !== undefined) budget.description = p.description;
        if (p.values !== undefined) budget.values = p.values;
        break;
      }

      case 'BudgetDeleted': {
        const p = event.payload as BudgetDeletedPayload;
        budgets.delete(p.budgetId);
        break;
      }

      case 'PriceAdded': {
        const p = event.payload as PriceAddedPayload;
        prices.set(p.priceId, {
          id: p.priceId,
          commodityId: p.commodityId,
          currency: p.currency,
          date: p.date,
          source: p.source,
          type: p.type,
          value: p.value,
        });
        break;
      }

      case 'PriceUpdated': {
        const p = event.payload as PriceUpdatedPayload;
        const price = prices.get(p.priceId);
        if (!price) break;
        if (p.value !== undefined) price.value = p.value;
        if (p.date !== undefined) price.date = p.date;
        if (p.source !== undefined) price.source = p.source;
        break;
      }

      case 'PriceDeleted': {
        const p = event.payload as PriceDeletedPayload;
        prices.delete(p.priceId);
        break;
      }

      case 'SettingsUpdated': {
        const p = event.payload as SettingsUpdatedPayload;
        if (p.homeCurrency !== undefined) settings = { ...settings, homeCurrency: p.homeCurrency };
        break;
      }

      case 'SettingUpdated': {
        const p = event.payload as SettingUpdatedPayload;
        if (p.key === 'homeCurrency' && typeof p.value === 'string') {
          settings = { ...settings, homeCurrency: p.value };
        }
        break;
      }

      case 'CredentialRevoked':
      case 'CredentialRefreshed':
        // handled by projectSettings only
        break;
    }
  }

  return { accounts, transactions, scheduledTransactions, budgets, prices, settings, lastSequence };
}

/**
 * Derive current settings values from the event log.
 * Processes both legacy SettingsUpdated and new SettingUpdated events.
 */
export function projectSettings(events: LedgerEvent[]): SettingsValues {
  const settings: SettingsValues = { ...SETTINGS_DEFAULTS, coinbaseAccountMapping: {} };
  const sorted = [...events].sort((a, b) => a.sequence - b.sequence);

  for (const event of sorted) {
    if (event.type === 'SettingsUpdated') {
      const p = event.payload as SettingsUpdatedPayload;
      if (p.homeCurrency !== undefined) settings.homeCurrency = p.homeCurrency;
    } else if (event.type === 'SettingUpdated') {
      const p = event.payload as SettingUpdatedPayload;
      (settings as unknown as Record<string, unknown>)[p.key] = p.value;
    } else if (event.type === 'CredentialRevoked') {
      const p = event.payload as CredentialRevokedPayload;
      (settings as unknown as Record<string, unknown>)[p.key] = null;
    } else if (event.type === 'CredentialRefreshed') {
      // No-op: CredentialRefreshed was used for OAuth token refresh; API key auth does not use it.
    }
  }

  return settings;
}

/**
 * Incrementally apply a single event to existing SettingsValues.
 * Used by EVENT_APPENDED to avoid full replay on every save.
 */
export function applySettingsEvent(current: SettingsValues, event: LedgerEvent): SettingsValues {
  if (event.type === 'SettingsUpdated') {
    const p = event.payload as SettingsUpdatedPayload;
    if (p.homeCurrency !== undefined) return { ...current, homeCurrency: p.homeCurrency };
    return current;
  }
  if (event.type === 'SettingUpdated') {
    const p = event.payload as SettingUpdatedPayload;
    return { ...current, [p.key]: p.value };
  }
  if (event.type === 'CredentialRevoked') {
    const p = event.payload as CredentialRevokedPayload;
    return { ...current, [p.key]: null };
  }
  if (event.type === 'CredentialRefreshed') {
    // No-op: CredentialRefreshed was used for OAuth token refresh; API key auth does not use it.
    return current;
  }
  return current;
}

/**
 * Apply a single event incrementally to an existing LedgerState.
 * Used by EVENT_APPENDED to avoid O(n) full replay on every save.
 */
export function applyEvent(current: LedgerState, event: LedgerEvent): LedgerState {
  const accounts = new Map(current.accounts);
  const transactions = new Map(current.transactions);
  const scheduledTransactions = new Map(current.scheduledTransactions);
  const budgets = new Map(current.budgets);
  const prices = new Map(current.prices);
  let settings: LedgerSettings = current.settings ?? { homeCurrency: 'USD' };
  const lastSequence = Math.max(current.lastSequence, event.sequence);

  switch (event.type) {
    case 'AccountCreated': {
      const p = event.payload as AccountCreatedPayload;
      accounts.set(p.accountId, {
        id: p.accountId, name: p.name, type: p.type, currency: p.currency,
        parentId: p.parentId, description: p.description,
        code: p.code ?? '', hidden: p.hidden ?? false, placeholder: p.placeholder,
        ...(p.commodity !== undefined ? { commodity: p.commodity } : {}),
        balance: p.openingBalance ?? 0,
      });
      break;
    }
    case 'AccountUpdated': {
      const p = event.payload as AccountUpdatedPayload;
      const existing = accounts.get(p.accountId);
      if (existing) {
        const updated = { ...existing };
        if (p.name !== undefined) updated.name = p.name;
        if (p.description !== undefined) updated.description = p.description;
        if (p.code !== undefined) updated.code = p.code;
        if (p.hidden !== undefined) updated.hidden = p.hidden;
        if (p.placeholder !== undefined) updated.placeholder = p.placeholder;
        if ('parentId' in p) updated.parentId = p.parentId ?? null;
        if (p.commodity !== undefined) updated.commodity = p.commodity;
        accounts.set(p.accountId, updated);
      }
      break;
    }
    case 'AccountDeleted': {
      const p = event.payload as AccountDeletedPayload;
      accounts.delete(p.accountId);
      if (p.reparentChildrenToId !== undefined) {
        for (const [id, account] of accounts) {
          if (account.parentId === p.accountId) {
            accounts.set(id, { ...account, parentId: p.reparentChildrenToId ?? null });
          }
        }
      }
      break;
    }
    case 'TransactionPosted': {
      const p = event.payload as TransactionPostedPayload;
      transactions.set(p.transactionId, {
        id: p.transactionId, date: p.date, dateEntered: p.dateEntered ?? event.timestamp,
        description: p.description, num: p.num ?? '', notes: p.notes,
        currency: p.currency, splits: p.splits.map((s) => ({ ...s })),
        voided: false, voidReason: null,
        ...(p.fitid !== undefined ? { fitid: p.fitid } : {}),
      });
      for (const split of p.splits) {
        const account = accounts.get(split.accountId);
        if (account) accounts.set(split.accountId, { ...account, balance: account.balance + split.amount });
      }
      break;
    }
    case 'TransactionVoided': {
      const p = event.payload as TransactionVoidedPayload;
      const tx = transactions.get(p.transactionId);
      if (tx && !tx.voided) {
        transactions.set(p.transactionId, { ...tx, voided: true, voidReason: p.voidReason });
        for (const split of tx.splits) {
          const account = accounts.get(split.accountId);
          if (account) accounts.set(split.accountId, { ...account, balance: account.balance - split.amount });
        }
      }
      break;
    }
    case 'TransactionDeleted': {
      const p = event.payload as TransactionDeletedPayload;
      const tx = transactions.get(p.transactionId);
      if (tx && !tx.voided) {
        for (const split of tx.splits) {
          const account = accounts.get(split.accountId);
          if (account) accounts.set(split.accountId, { ...account, balance: account.balance - split.amount });
        }
      }
      transactions.delete(p.transactionId);
      break;
    }
    case 'SplitReconciled': {
      const p = event.payload as SplitReconciledPayload;
      const tx = transactions.get(p.transactionId);
      if (tx) {
        transactions.set(p.transactionId, {
          ...tx,
          splits: tx.splits.map((s) =>
            s.splitId === p.splitId
              ? { ...s, reconcileState: p.reconcileState, reconcileDate: p.reconcileDate }
              : s
          ),
        });
      }
      break;
    }
    case 'ReconciliationPosted':
    case 'ReconciliationCompleted': {
      const p = event.payload as ReconciliationPostedPayload | ReconciliationCompletedPayload;
      const reconciledIds = new Set(p.reconciledSplitIds);
      for (const [txId, tx] of transactions) {
        if (tx.splits.some((s) => s.accountId === p.accountId && reconciledIds.has(s.splitId))) {
          transactions.set(txId, {
            ...tx,
            splits: tx.splits.map((s) =>
              s.accountId === p.accountId && reconciledIds.has(s.splitId)
                ? { ...s, reconcileState: 'y', reconcileDate: p.statementDate }
                : s
            ),
          });
        }
      }
      break;
    }
    case 'ReconciliationStarted': break;
    case 'ScheduledTransactionCreated': {
      const p = event.payload as ScheduledTransactionCreatedPayload;
      scheduledTransactions.set(p.scheduledTransactionId, {
        id: p.scheduledTransactionId, name: p.name, enabled: p.enabled,
        autoCreate: p.autoCreate, autoCreateDaysInAdvance: p.autoCreateDaysInAdvance,
        advanceReminderDays: p.advanceReminderDays,
        templateTransaction: p.templateTransaction, frequency: p.frequency,
      });
      break;
    }
    case 'ScheduledTransactionUpdated': {
      const p = event.payload as ScheduledTransactionUpdatedPayload;
      const st = scheduledTransactions.get(p.scheduledTransactionId);
      if (st) {
        const updated = { ...st };
        if (p.name !== undefined) updated.name = p.name;
        if (p.enabled !== undefined) updated.enabled = p.enabled;
        if (p.autoCreate !== undefined) updated.autoCreate = p.autoCreate;
        if (p.autoCreateDaysInAdvance !== undefined) updated.autoCreateDaysInAdvance = p.autoCreateDaysInAdvance;
        if (p.advanceReminderDays !== undefined) updated.advanceReminderDays = p.advanceReminderDays;
        if (p.templateTransaction !== undefined) updated.templateTransaction = p.templateTransaction;
        if (p.frequency !== undefined) updated.frequency = p.frequency;
        scheduledTransactions.set(p.scheduledTransactionId, updated);
      }
      break;
    }
    case 'ScheduledTransactionDeleted': {
      const p = event.payload as ScheduledTransactionDeletedPayload;
      scheduledTransactions.delete(p.scheduledTransactionId);
      break;
    }
    case 'BudgetCreated': {
      const p = event.payload as BudgetCreatedPayload;
      budgets.set(p.budgetId, {
        id: p.budgetId, name: p.name, description: p.description,
        periodType: p.periodType, numPeriods: p.numPeriods,
        startDate: p.startDate, values: p.values,
      });
      break;
    }
    case 'BudgetUpdated': {
      const p = event.payload as BudgetUpdatedPayload;
      const budget = budgets.get(p.budgetId);
      if (budget) {
        const updated = { ...budget };
        if (p.name !== undefined) updated.name = p.name;
        if (p.description !== undefined) updated.description = p.description;
        if (p.values !== undefined) updated.values = p.values;
        budgets.set(p.budgetId, updated);
      }
      break;
    }
    case 'BudgetDeleted': {
      const p = event.payload as BudgetDeletedPayload;
      budgets.delete(p.budgetId);
      break;
    }
    case 'PriceAdded': {
      const p = event.payload as PriceAddedPayload;
      prices.set(p.priceId, {
        id: p.priceId, commodityId: p.commodityId, currency: p.currency,
        date: p.date, source: p.source, type: p.type, value: p.value,
      });
      break;
    }
    case 'PriceUpdated': {
      const p = event.payload as PriceUpdatedPayload;
      const price = prices.get(p.priceId);
      if (price) {
        const updated = { ...price };
        if (p.value !== undefined) updated.value = p.value;
        if (p.date !== undefined) updated.date = p.date;
        if (p.source !== undefined) updated.source = p.source;
        prices.set(p.priceId, updated);
      }
      break;
    }
    case 'PriceDeleted': {
      const p = event.payload as PriceDeletedPayload;
      prices.delete(p.priceId);
      break;
    }
    case 'SettingsUpdated': {
      const p = event.payload as SettingsUpdatedPayload;
      if (p.homeCurrency !== undefined) settings = { ...settings, homeCurrency: p.homeCurrency };
      break;
    }
    case 'SettingUpdated': {
      const p = event.payload as SettingUpdatedPayload;
      if (p.key === 'homeCurrency' && typeof p.value === 'string') {
        settings = { ...settings, homeCurrency: p.value };
      }
      break;
    }
    case 'CredentialRevoked':
    case 'CredentialRefreshed':
      break;
  }

  return { accounts, transactions, scheduledTransactions, budgets, prices, settings, lastSequence };
}

/**
 * Format a minor-unit amount as a display string.
 * Uses the currency's correct decimal places (e.g. 0 for JPY, 2 for USD, 3 for KWD).
 */
export function formatAmount(
  minorUnits: number,
  currency = 'USD',
  locale = getDisplayLocale(),
): string {
  const decimalPlaces = getCurrencyDecimalPlaces(currency);
  const value = minorUnits / Math.pow(10, decimalPlaces);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  } catch {
    return value.toFixed(decimalPlaces);
  }
}

/** Format just the numeric part without currency symbol. */
export function formatAmountPlain(minorUnits: number, currency = 'USD'): string {
  const decimalPlaces = getCurrencyDecimalPlaces(currency);
  return (Math.abs(minorUnits) / Math.pow(10, decimalPlaces)).toFixed(decimalPlaces);
}

/** Get all transactions that touch a given account (by accountId). */
export function getAccountTransactions(
  state: LedgerState,
  accountId: string,
): Transaction[] {
  const result: Transaction[] = [];
  for (const tx of state.transactions.values()) {
    if (!tx.voided && tx.splits.some((s) => s.accountId === accountId)) {
      result.push(tx);
    }
  }
  return result.sort((a, b) =>
    a.date !== b.date ? a.date.localeCompare(b.date) : a.dateEntered.localeCompare(b.dateEntered),
  );
}

/** Compute running balance for a transaction list relative to an account. */
export function computeRunningBalance(
  transactions: Transaction[],
  accountId: string,
): { tx: Transaction; runningBalance: number }[] {
  let running = 0;
  return transactions.map((tx) => {
    const split = tx.splits.find((s) => s.accountId === accountId);
    running += split?.amount ?? 0;
    return { tx, runningBalance: running };
  });
}

/** Merge new events into an existing sorted event list, deduplicating by sequence. */
export function mergeEvents(
  existing: LedgerEvent[],
  incoming: LedgerEvent[],
): LedgerEvent[] {
  const bySeq = new Map<number, LedgerEvent>();
  for (const e of existing) bySeq.set(e.sequence, e);
  for (const e of incoming) bySeq.set(e.sequence, e);
  return [...bySeq.values()].sort((a, b) => a.sequence - b.sequence);
}

/**
 * Compute the next occurrence of a scheduled transaction after a given date.
 * Returns ISO date string or null if the schedule has ended.
 */
export function computeNextOccurrence(
  freq: { type: string; interval: number; startDate: string; endDate: string | null },
  afterDate: string,
): string | null {
  const start = new Date(freq.startDate + 'T00:00:00');
  let candidate = new Date(start);
  const after = new Date(afterDate + 'T00:00:00');

  const advance = () => {
    switch (freq.type) {
      case 'once': return null;
      case 'daily': candidate.setDate(candidate.getDate() + freq.interval); break;
      case 'weekly': candidate.setDate(candidate.getDate() + 7 * freq.interval); break;
      case 'biweekly': candidate.setDate(candidate.getDate() + 14); break;
      case 'monthly': candidate.setMonth(candidate.getMonth() + freq.interval); break;
      case 'quarterly': candidate.setMonth(candidate.getMonth() + 3 * freq.interval); break;
      case 'yearly': candidate.setFullYear(candidate.getFullYear() + freq.interval); break;
    }
    return candidate.toISOString().slice(0, 10);
  };

  if (freq.type === 'once') {
    if (start > after) return freq.startDate;
    return null;
  }

  if (freq.interval <= 0) return null;

  while (candidate <= after) {
    const next = advance();
    if (!next) return null;
  }

  const result = candidate.toISOString().slice(0, 10);
  if (freq.endDate && result > freq.endDate) return null;
  return result;
}

/** Account types that are assets (natural debit balance). */
export const ASSET_TYPES = new Set<string>([
  'ASSET', 'BANK', 'CASH', 'INVESTMENT', 'RECEIVABLE',
  'MUTUAL_FUND', 'STOCK', 'ACCOUNTS_RECEIVABLE',
]);

/** Account types that are liabilities/credit (natural credit balance). */
export const LIABILITY_TYPES = new Set<string>([
  'LIABILITY', 'CREDIT', 'PAYABLE', 'CREDIT_CARD', 'ACCOUNTS_PAYABLE',
]);

/** Account types that are income (natural credit balance). */
export const INCOME_TYPES = new Set<string>(['INCOME']);

/** Account types that are expenses (natural debit balance). */
export const EXPENSE_TYPES = new Set<string>(['EXPENSE']);

/** Return the debit/credit column labels for a given account type. */
export function getRegisterColumnNames(type: string): { debit: string; credit: string } {
  switch (type) {
    case 'BANK': return { debit: 'Withdrawal', credit: 'Deposit' };
    case 'CREDIT_CARD':
    case 'CREDIT': return { debit: 'Charge', credit: 'Payment' };
    case 'INCOME': return { debit: 'Income', credit: 'Charge' };
    case 'EXPENSE': return { debit: 'Expense', credit: 'Rebate' };
    default: return { debit: 'Decrease', credit: 'Increase' };
  }
}

// i18n contract: stable codes for the debit/credit register column headers,
// which read differently per account type. Components translate via
// t(`accounting:registerColumns.${code}`). Kept in lockstep with the English
// getRegisterColumnNames above.
export function getRegisterColumnKeys(type: string): { debit: string; credit: string } {
  switch (type) {
    case 'BANK': return { debit: 'withdrawal', credit: 'deposit' };
    case 'CREDIT_CARD':
    case 'CREDIT': return { debit: 'charge', credit: 'payment' };
    case 'INCOME': return { debit: 'income', credit: 'charge' };
    case 'EXPENSE': return { debit: 'expense', credit: 'rebate' };
    default: return { debit: 'decrease', credit: 'increase' };
  }
}
